<template>
  <BaseModalEditCreate
    mode="edit"
    :disable="mode == 'view'"
    :hide-save-button="mode == 'view'"
    :is-modified="false"
    :close-on-outside-click="false"
    max-width="full"
    @save="onSave"
    @dismiss-changes="onCancel">
    <template #title>
      <span class="text-2xl font-semibold"> {{ title }} </span>
    </template>
    <template #form>
      <div class="overflow-hidden h-full grid grid-rows-[auto_1fr] gap-7">
        <div class="font-light">
          {{ mode == 'edit' ? $t('routes.customizeOrderText') : $t('routes.reviewOrderText') }}
        </div>
        <div class="w-full h-full overflow-x-auto">
          <div class="flex flex-row flex-1 w-full h-full gap-2">
            <div v-for="(day, index) in weekDays" :key="index" class="flex-1">
              <div class="flex flex-col h-full rounded-lg">
                <div
                  class="z-20 px-3 py-4 rounded-t-lg"
                  :class="index % 2 == 0 ? '  bg-powder-blue-mid' : ' bg-powder-gray-mid'">
                  <p class="text-xl text-base-blue">{{ day.name }}</p>
                </div>

                <PerfectScrollbar
                  class="h-full pb-1.5"
                  :class="index % 2 == 0 ? ' bg-cyan-light' : ' bg-powder-gray-light'">
                  <draggable
                    v-model="day.traderSites"
                    ghost-class="ghost"
                    animation="300"
                    :disabled="mode == 'view'">
                    <template #item="{ element }">
                      <div class="draggable-item">
                        <div
                          class="flex flex-col p-1.5 mx-2 mt-2 transition rounded-md bg-white hover:bg-cyan-light"
                          :class="mode == 'edit' ? 'cursor-pointer' : ' pointer-events-none'">
                          <span class="text-sm text-base-blue">{{ element.name }}</span>
                          <span class="text-sm font-light text-slate-500">{{
                            element.address
                          }}</span>
                        </div>
                      </div>
                    </template>
                  </draggable>
                </PerfectScrollbar>
              </div>
            </div>
          </div>
        </div>
      </div>
      <BaseDrawerConfirm
        :is-active="confirmModalActive"
        @cancel="confirmModalActive = false"
        @confirm="onDiscardChanges"></BaseDrawerConfirm>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import BaseDrawerConfirm from '../common/BaseDrawerConfirm.vue';
import { RecordMode } from '@/types/Base';
import type { RouteTraderSite } from '@/types/Routes';
import { deepCopy } from '@/utils/data';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import draggable from 'vuedraggable';

const { t: $t } = useI18n({ useScope: 'global' });
const emit = defineEmits(['close', 'save']);

defineProps<{
  title: string;
  mode: 'view' | 'edit';
}>();

const routeTraderSites = defineModel<RouteTraderSite[]>({ default: [] });

const modifiedData = ref<RouteTraderSite[]>([]);
onMounted(async () => {
  modifiedData.value = JSON.parse(
    JSON.stringify(
      routeTraderSites.value?.filter(
        (x) =>
          !x.deleted &&
          x.recordMode != RecordMode.Deleted &&
          x.recordMode != RecordMode.Restored &&
          x.visitPlan
      )
    )
  );

  modifiedData.value.forEach((rts) => {
    if (rts.visitPlan && rts.visitPlan.code && rts.traderSite) assignSitesToDays(rts);
  });
  sortDaySites();
  unmodifiedWeekDays.value = deepCopy<
    {
      index: number;
      name: string;
      traderSites: { id: string; name: string; address: string; sequence: number | null }[];
    }[]
  >(weekDays.value);
});

const unmodifiedWeekDays = ref<
  {
    index: number;
    name: string;
    traderSites: { id: string; name: string; address: string; sequence: number | null }[];
  }[]
>([]);

function assignSitesToDays(rts: RouteTraderSite) {
  const code = rts.visitPlan?.code;
  const site = rts.traderSite;

  const codeChars = [...(code || '')];
  codeChars.forEach((char, charIndex) => {
    if (char == '0') return;

    const index = weekDays.value.findIndex((x) => x.index == charIndex % 7);
    // add the tradersite on the corresponding weekDay traderSites collection
    weekDays.value[index].traderSites.splice(0, 0, {
      id: site?.id || '',
      name: site?.name || '',
      address: `${site?.address}, ${site?.city}` || '',
      sequence: rts[`sequence${index + 1}` as keyof RouteTraderSite] as number | null,
    });
    weekDays.value[index] = {
      ...weekDays.value[index],
      traderSites: weekDays.value[index].traderSites,
    };
  });
}

function sortDaySites() {
  weekDays.value.forEach((weekDay) => {
    weekDay.traderSites.sort((a, b) => {
      return (a.sequence ?? Infinity) - (b.sequence ?? Infinity);
    });
  });
}

function onSave() {
  modifiedData.value.forEach((rts) => {
    const codeChars = [...(rts.visitPlan?.code || '')];
    codeChars.forEach((char, charIndex) => {
      if (char == '0') return;

      const index = weekDays.value.findIndex((x) => x.index == charIndex % 7);
      const key = `sequence${index + 1}` as keyof RouteTraderSite;
      (rts[key] as number | null) =
        weekDays.value[index].traderSites.findIndex((x) => x.id == rts.traderSiteId) + 1;
      if (routeTraderSites.value) {
        const original = routeTraderSites.value.find((x) => x.id == rts.id);
        if (original && original[key] != rts[key] && !original.recordMode)
          rts.recordMode = RecordMode.Modified;
      }
    });
    const index = routeTraderSites.value?.findIndex((x) => x.id == rts.id);
    routeTraderSites.value[index] = {
      ...routeTraderSites.value[index],
      ...rts,
    };
  });
  emit('save');
}

function onCancel() {
  if (!isModified.value) emit('close');
  confirmModalActive.value = true;
}

function onDiscardChanges() {
  weekDays.value = deepCopy<
    {
      index: number;
      name: string;
      traderSites: { id: string; name: string; address: string; sequence: number | null }[];
    }[]
  >(unmodifiedWeekDays.value);
  emit('close');
}

const weekDays = ref<
  {
    index: number;
    name: string;
    traderSites: { id: string; name: string; address: string; sequence: number | null }[];
  }[]
>([
  {
    index: 0,
    name: $t('date.days.1'),
    traderSites: [],
  },
  {
    index: 1,
    name: $t('date.days.2'),
    traderSites: [],
  },
  {
    index: 2,
    name: $t('date.days.3'),
    traderSites: [],
  },
  {
    index: 3,
    name: $t('date.days.4'),
    traderSites: [],
  },
  {
    index: 4,
    name: $t('date.days.5'),
    traderSites: [],
  },
  {
    index: 5,
    name: $t('date.days.6'),
    traderSites: [],
  },
  {
    index: 6,
    name: $t('date.days.0'),
    traderSites: [],
  },
]);

const confirmModalActive = ref(false);

const isModified = ref(false);

watch(
  () => weekDays.value,
  (newData) => {
    isModified.value =
      JSON.stringify(newData) !==
      JSON.stringify(JSON.parse(JSON.stringify(unmodifiedWeekDays.value)));
  },
  { deep: true }
);
</script>

<style>
.ghost {
  @apply bg-theme-500 opacity-40 m-0 p-0 h-0.5 text-transparent;
}
</style>
